/**
 * First-run bootstrap for the Mochia Spreadsheet.
 *
 * Per design.md:
 * 1. Drive lookup for `Mochia Data` → if found, return id.
 * 2. Otherwise create the spreadsheet, add 6 tabs, drop the placeholder
 *    `Sheet1`, write headers.
 * 3. Whether found or freshly created, call `verifyAndRepair` to seed (or
 *    top up) the 18 system categories (14 expense + 4 income) + 71 system
 *    sub-categories + default book + 4 meta defaults. `verifyAndRepair` is
 *    the SINGLE source of truth for seed contents and is fully idempotent —
 *    running it twice produces no further writes
 *    (Section 6 of `improve-error-ux-and-defaults`).
 *
 * Sharp edges:
 *  - `verifyAndRepair` MUST be idempotent: a fully-seeded sheet sees no
 *    writes. We diff by stable id (`id` for category/book, `key` for meta)
 *    so user-edited rows still count as "present".
 *  - We NEVER overwrite cells the user may have edited; only fill missing
 *    rows.
 *  - On the bootstrap-from-empty path, `verifyAndRepair` issues exactly one
 *    `valuesBatchUpdate` call covering all three tabs, replacing the prior
 *    three sequential `valuesAppend` round-trips that left a partial-seed
 *    window if the second / third call timed out.
 *
 * Seeded category names + color tokens are ported from
 * `design/project/shared.jsx`'s `CATEGORIES` dictionary.
 */

import type { SheetsClient } from './client';
import {
  POINTER_SPREADSHEET_NAME,
  SPREADSHEET_NAME,
  SUPPORTED_SCHEMA_VERSION,
  TABS,
  TAB_NAMES,
  headerRange,
  tabRange,
  type TabName,
} from './schema';
import { nowIso, objectToRow, rowToObject } from './row-utils';

export interface BootstrapResult {
  spreadsheetId: string;
  created: boolean;
  /**
   * The categories/books/meta rows read by the single `valuesBatchGet` call
   * that fed `verifyAndRepair`, for callers (e.g. `checkSchemaVersion`) to
   * reuse instead of re-reading the same tabs (data-layer spec requirement
   * "Bootstrap Read Deduplication"). `null` when there is nothing safe to
   * reuse: the spreadsheet was just created (`created: true`), or
   * `verifyAndRepair` topped up the `meta` tab during this run, which makes
   * the snapshot read before that write stale.
   */
  preloaded: PreloadedBootstrapRows | null;
}

/** Public seed-category shape — re-exported so the new-book wizard can
 *  read the same dictionary when copying defaults into a fresh book. */
export interface SeedCategory {
  id: string;
  name: string;
  color_token: string;
  icon: string;
  sort_order: number;
  kind: 'expense' | 'income';
}

export const SEED_CATEGORIES: ReadonlyArray<SeedCategory> = [
  // 14 expense + 4 income mains — two-char names match the sheet contents
  // a real user has after 16.5 mo of usage. sort_order uses 100-spacing so
  // future inserts don't have to renumber. See change
  // `reorganize-category-taxonomy` for the v1→v2 migration rationale.
  { id: 'cat-food', name: '飲食', color_token: '--cat-food', icon: 'lucide:utensils', sort_order: 100, kind: 'expense' },
  { id: 'cat-daily', name: '日常', color_token: '--cat-daily', icon: 'lucide:shopping-bag', sort_order: 200, kind: 'expense' },
  { id: 'cat-transit', name: '交通', color_token: '--cat-transit', icon: 'lucide:car', sort_order: 300, kind: 'expense' },
  { id: 'cat-home', name: '住宅', color_token: '--cat-home', icon: 'lucide:house', sort_order: 400, kind: 'expense' },
  { id: 'cat-fun', name: '娛樂', color_token: '--cat-fun', icon: 'lucide:gamepad-2', sort_order: 500, kind: 'expense' },
  { id: 'cat-health', name: '醫療', color_token: '--cat-health', icon: 'lucide:stethoscope', sort_order: 600, kind: 'expense' },
  { id: 'cat-pet', name: '寵物', color_token: '--cat-pet', icon: 'lucide:paw-print', sort_order: 700, kind: 'expense' },
  { id: 'cat-travel', name: '旅遊', color_token: '--cat-travel', icon: 'lucide:plane', sort_order: 800, kind: 'expense' },
  { id: 'cat-sub', name: '訂閱', color_token: '--cat-sub', icon: 'lucide:tv', sort_order: 900, kind: 'expense' },
  { id: 'cat-tax', name: '稅務', color_token: '--cat-tax', icon: 'lucide:receipt', sort_order: 1000, kind: 'expense' },
  { id: 'cat-fin', name: '金融', color_token: '--cat-fin', icon: 'lucide:credit-card', sort_order: 1100, kind: 'expense' },
  { id: 'cat-edu', name: '教育', color_token: '--cat-edu', icon: 'lucide:graduation-cap', sort_order: 1200, kind: 'expense' },
  { id: 'cat-social', name: '送禮', color_token: '--cat-other', icon: 'lucide:gift', sort_order: 1300, kind: 'expense' },
  { id: 'cat-other', name: '其他', color_token: '--cat-other', icon: 'lucide:tag', sort_order: 1400, kind: 'expense' },
  { id: 'cat-salary', name: '薪資', color_token: '--matcha-500', icon: 'lucide:briefcase', sort_order: 1500, kind: 'income' },
  { id: 'cat-invest', name: '投資', color_token: '--cat-transit', icon: 'lucide:trending-up', sort_order: 1600, kind: 'income' },
  { id: 'cat-gift', name: '禮金', color_token: '--honey-500', icon: 'lucide:piggy-bank', sort_order: 1700, kind: 'income' },
  { id: 'cat-income-other', name: '其他收入', color_token: '--cat-other', icon: 'lucide:wallet', sort_order: 1800, kind: 'income' },
];

export interface SeedSubCategory {
  id: string;
  parent_id: string;
  name: string;
  icon: string;
  sort_order: number;
  kind: 'expense' | 'income';
}

/**
 * All sub-categories — 66 expense + 5 income = 71 total. Names and structure
 * derived from 16.5 mo of real usage data (2127 tx) per change
 * `reorganize-category-taxonomy`. Each id is `<parent>-<slug>` so they're
 * stable across migrations and collision-free even if a user creates a
 * same-named custom sub later. sort_order uses 100-spacing so future inserts
 * don't require renumbering.
 */
export const SEED_SUBCATEGORIES: ReadonlyArray<SeedSubCategory> = [
  // 飲食 (cat-food) — 7
  { id: 'sub-food-breakfast', parent_id: 'cat-food', name: '早餐', icon: 'lucide:sandwich', sort_order: 100, kind: 'expense' },
  { id: 'sub-food-lunch', parent_id: 'cat-food', name: '午餐', icon: 'lucide:utensils-crossed', sort_order: 200, kind: 'expense' },
  { id: 'sub-food-dinner', parent_id: 'cat-food', name: '晚餐', icon: 'lucide:utensils', sort_order: 300, kind: 'expense' },
  { id: 'sub-food-drink', parent_id: 'cat-food', name: '咖啡飲料', icon: 'lucide:coffee', sort_order: 400, kind: 'expense' },
  { id: 'sub-food-snack', parent_id: 'cat-food', name: '點心宵夜', icon: 'lucide:cookie', sort_order: 500, kind: 'expense' },
  { id: 'sub-food-mart', parent_id: 'cat-food', name: '採買', icon: 'lucide:salad', sort_order: 600, kind: 'expense' },
  { id: 'sub-food-social', parent_id: 'cat-food', name: '聚餐', icon: 'lucide:wine', sort_order: 700, kind: 'expense' },
  // 日常 (cat-daily) — 7
  { id: 'sub-daily-hygiene', parent_id: 'cat-daily', name: '衛生用品', icon: 'lucide:bath', sort_order: 100, kind: 'expense' },
  { id: 'sub-daily-skin', parent_id: 'cat-daily', name: '美妝保養', icon: 'lucide:sparkles', sort_order: 200, kind: 'expense' },
  { id: 'sub-daily-telecom', parent_id: 'cat-daily', name: '電信費', icon: 'lucide:phone', sort_order: 300, kind: 'expense' },
  { id: 'sub-daily-cloth', parent_id: 'cat-daily', name: '服飾配件', icon: 'lucide:shirt', sort_order: 400, kind: 'expense' },
  { id: 'sub-daily-3c', parent_id: 'cat-daily', name: '3C 數位', icon: 'lucide:laptop', sort_order: 500, kind: 'expense' },
  { id: 'sub-daily-hair', parent_id: 'cat-daily', name: '美髮美甲', icon: 'lucide:scissors', sort_order: 600, kind: 'expense' },
  { id: 'sub-daily-clean', parent_id: 'cat-daily', name: '居家清潔', icon: 'lucide:trash-2', sort_order: 700, kind: 'expense' },
  // 交通 (cat-transit) — 5
  { id: 'sub-transit-park', parent_id: 'cat-transit', name: '停車', icon: 'lucide:parking-circle', sort_order: 100, kind: 'expense' },
  { id: 'sub-transit-fuel', parent_id: 'cat-transit', name: '油費', icon: 'lucide:fuel', sort_order: 200, kind: 'expense' },
  { id: 'sub-transit-taxi', parent_id: 'cat-transit', name: '計程車', icon: 'lucide:car', sort_order: 300, kind: 'expense' },
  { id: 'sub-transit-mass', parent_id: 'cat-transit', name: '大眾運輸', icon: 'lucide:bus', sort_order: 400, kind: 'expense' },
  { id: 'sub-transit-maint', parent_id: 'cat-transit', name: '車輛保養', icon: 'lucide:wrench', sort_order: 500, kind: 'expense' },
  // 住宅 (cat-home) — 7
  { id: 'sub-home-mgmt', parent_id: 'cat-home', name: '管理費', icon: 'lucide:building', sort_order: 100, kind: 'expense' },
  { id: 'sub-home-net', parent_id: 'cat-home', name: '網路', icon: 'lucide:wifi', sort_order: 200, kind: 'expense' },
  { id: 'sub-home-water', parent_id: 'cat-home', name: '水費', icon: 'lucide:droplets', sort_order: 300, kind: 'expense' },
  { id: 'sub-home-electric', parent_id: 'cat-home', name: '電費', icon: 'lucide:lightbulb', sort_order: 400, kind: 'expense' },
  { id: 'sub-home-gas', parent_id: 'cat-home', name: '瓦斯', icon: 'lucide:flame', sort_order: 500, kind: 'expense' },
  { id: 'sub-home-furniture', parent_id: 'cat-home', name: '家具家電', icon: 'lucide:sofa', sort_order: 600, kind: 'expense' },
  { id: 'sub-home-repair', parent_id: 'cat-home', name: '修繕', icon: 'lucide:paint-roller', sort_order: 700, kind: 'expense' },
  // 娛樂 (cat-fun) — 4
  { id: 'sub-fun-sport', parent_id: 'cat-fun', name: '運動健身', icon: 'lucide:dumbbell', sort_order: 100, kind: 'expense' },
  { id: 'sub-fun-show', parent_id: 'cat-fun', name: '電影演唱會', icon: 'lucide:ticket', sort_order: 200, kind: 'expense' },
  { id: 'sub-fun-night', parent_id: 'cat-fun', name: '夜生活', icon: 'lucide:disc', sort_order: 300, kind: 'expense' },
  { id: 'sub-fun-hobby', parent_id: 'cat-fun', name: '興趣收藏', icon: 'lucide:star', sort_order: 400, kind: 'expense' },
  // 醫療 (cat-health) — 4
  { id: 'sub-health-clinic', parent_id: 'cat-health', name: '看診掛號', icon: 'lucide:hospital', sort_order: 100, kind: 'expense' },
  { id: 'sub-health-supp', parent_id: 'cat-health', name: '保健食品', icon: 'lucide:heart-pulse', sort_order: 200, kind: 'expense' },
  { id: 'sub-health-drug', parent_id: 'cat-health', name: '藥品', icon: 'lucide:pill', sort_order: 300, kind: 'expense' },
  { id: 'sub-health-checkup', parent_id: 'cat-health', name: '健檢', icon: 'lucide:activity', sort_order: 400, kind: 'expense' },
  // 寵物 (cat-pet) — 7
  { id: 'sub-pet-toy', parent_id: 'cat-pet', name: '玩具用品', icon: 'lucide:bone', sort_order: 100, kind: 'expense' },
  { id: 'sub-pet-can', parent_id: 'cat-pet', name: '罐頭鮮食', icon: 'lucide:fish', sort_order: 200, kind: 'expense' },
  { id: 'sub-pet-litter', parent_id: 'cat-pet', name: '貓砂耗材', icon: 'lucide:cat', sort_order: 300, kind: 'expense' },
  { id: 'sub-pet-clinic', parent_id: 'cat-pet', name: '醫療看診', icon: 'lucide:stethoscope', sort_order: 400, kind: 'expense' },
  { id: 'sub-pet-vac', parent_id: 'cat-pet', name: '疫苗驅蟲', icon: 'lucide:pill', sort_order: 500, kind: 'expense' },
  { id: 'sub-pet-food', parent_id: 'cat-pet', name: '飼料', icon: 'lucide:dog', sort_order: 600, kind: 'expense' },
  { id: 'sub-pet-supplement', parent_id: 'cat-pet', name: '保健食品', icon: 'lucide:heart-pulse', sort_order: 700, kind: 'expense' },
  // 旅遊 (cat-travel) — 6
  { id: 'sub-travel-air', parent_id: 'cat-travel', name: '機票', icon: 'lucide:plane-takeoff', sort_order: 100, kind: 'expense' },
  { id: 'sub-travel-stay', parent_id: 'cat-travel', name: '住宿', icon: 'lucide:bed-double', sort_order: 200, kind: 'expense' },
  { id: 'sub-travel-local', parent_id: 'cat-travel', name: '當地交通', icon: 'lucide:train', sort_order: 300, kind: 'expense' },
  { id: 'sub-travel-gift', parent_id: 'cat-travel', name: '伴手禮', icon: 'lucide:cake', sort_order: 400, kind: 'expense' },
  { id: 'sub-travel-bag', parent_id: 'cat-travel', name: '行李寄放', icon: 'lucide:luggage', sort_order: 500, kind: 'expense' },
  { id: 'sub-travel-tix', parent_id: 'cat-travel', name: '門票景點', icon: 'lucide:map-pin', sort_order: 600, kind: 'expense' },
  // 訂閱 (cat-sub) — 4
  { id: 'sub-sub-video', parent_id: 'cat-sub', name: '影音串流', icon: 'lucide:film', sort_order: 100, kind: 'expense' },
  { id: 'sub-sub-cloud', parent_id: 'cat-sub', name: '雲端服務', icon: 'lucide:cloud', sort_order: 200, kind: 'expense' },
  { id: 'sub-sub-software', parent_id: 'cat-sub', name: '軟體訂閱', icon: 'lucide:repeat', sort_order: 300, kind: 'expense' },
  { id: 'sub-sub-content', parent_id: 'cat-sub', name: '內容訂閱', icon: 'lucide:book', sort_order: 400, kind: 'expense' },
  // 稅務 (cat-tax) — 3
  { id: 'sub-tax-house', parent_id: 'cat-tax', name: '房屋稅', icon: 'lucide:home', sort_order: 100, kind: 'expense' },
  { id: 'sub-tax-land', parent_id: 'cat-tax', name: '地價稅', icon: 'lucide:map', sort_order: 200, kind: 'expense' },
  { id: 'sub-tax-income', parent_id: 'cat-tax', name: '綜所稅', icon: 'lucide:percent', sort_order: 300, kind: 'expense' },
  // 金融 (cat-fin) — 3
  { id: 'sub-fin-mortgage', parent_id: 'cat-fin', name: '房貸', icon: 'lucide:landmark', sort_order: 100, kind: 'expense' },
  { id: 'sub-fin-carloan', parent_id: 'cat-fin', name: '車貸', icon: 'lucide:car', sort_order: 200, kind: 'expense' },
  { id: 'sub-fin-insurance', parent_id: 'cat-fin', name: '保險', icon: 'lucide:lock', sort_order: 300, kind: 'expense' },
  // 教育 (cat-edu) — 3
  { id: 'sub-edu-book', parent_id: 'cat-edu', name: '書籍', icon: 'lucide:book-open', sort_order: 100, kind: 'expense' },
  { id: 'sub-edu-course', parent_id: 'cat-edu', name: '課程進修', icon: 'lucide:pencil', sort_order: 200, kind: 'expense' },
  { id: 'sub-edu-online', parent_id: 'cat-edu', name: '課程訂閱', icon: 'lucide:globe', sort_order: 300, kind: 'expense' },
  // 送禮 (cat-social) — 5
  { id: 'sub-social-redenv', parent_id: 'cat-social', name: '紅包', icon: 'lucide:banknote', sort_order: 100, kind: 'expense' },
  { id: 'sub-social-festival', parent_id: 'cat-social', name: '節日送禮', icon: 'lucide:gift', sort_order: 200, kind: 'expense' },
  { id: 'sub-social-bday', parent_id: 'cat-social', name: '生日禮物', icon: 'lucide:cake', sort_order: 300, kind: 'expense' },
  { id: 'sub-social-elder', parent_id: 'cat-social', name: '孝親費', icon: 'lucide:heart', sort_order: 400, kind: 'expense' },
  { id: 'sub-social-misc', parent_id: 'cat-social', name: '一般贈禮', icon: 'lucide:star', sort_order: 500, kind: 'expense' },
  // 其他 (cat-other) — 1
  { id: 'sub-other-todo', parent_id: 'cat-other', name: '待分類', icon: 'lucide:hash', sort_order: 100, kind: 'expense' },
  // 薪資 (cat-salary) — 2
  { id: 'sub-salary-monthly', parent_id: 'cat-salary', name: '月薪', icon: 'lucide:calendar', sort_order: 100, kind: 'income' },
  { id: 'sub-salary-bonus', parent_id: 'cat-salary', name: '獎金', icon: 'lucide:coins', sort_order: 200, kind: 'income' },
  // 投資 (cat-invest) — 1
  { id: 'sub-invest-div', parent_id: 'cat-invest', name: '股利', icon: 'lucide:line-chart', sort_order: 100, kind: 'income' },
  // 禮金 (cat-gift) — 2
  { id: 'sub-gift-redenv', parent_id: 'cat-gift', name: '紅包', icon: 'lucide:banknote', sort_order: 100, kind: 'income' },
  { id: 'sub-gift-elder', parent_id: 'cat-gift', name: '長輩給予', icon: 'lucide:heart', sort_order: 200, kind: 'income' },
  // 其他收入 (cat-income-other) — 0 (used with empty subcategory_id)
];

interface SeedBook {
  id: string;
  name: string;
  color_token: string;
  type: 'personal' | 'family' | 'project';
  is_default: 'true' | 'false';
}

/**
 * Default-seed: a single 家庭 book. Per dogfood feedback, 專案 was noise on
 * a fresh workbook — users either don't need it or prefer to create their
 * own with a meaningful name (「2026 東京」, 「網球培訓」). Removing the
 * 專案 seed keeps the cold-start UI as minimal as possible. Existing users
 * with a `book-project` row keep it; nothing here removes it.
 *
 * The legacy `book-personal` id is retained as a v3/v4 migration target —
 * see `migrateToV4`. We do NOT seed it on a fresh workbook.
 */
const SEED_BOOKS: ReadonlyArray<SeedBook> = [
  {
    id: 'book-family',
    name: '家庭',
    color_token: '--matcha-500',
    type: 'family',
    is_default: 'true',
  },
];

interface SeedMeta {
  key: string;
  value: string;
}

function buildSeedMeta(): ReadonlyArray<SeedMeta> {
  return [
    { key: 'schema_version', value: String(SUPPORTED_SCHEMA_VERSION) },
    { key: 'primary_currency', value: 'TWD' },
    { key: 'auto_logout_minutes', value: '5' },
    {
      key: 'notif_settings',
      value: JSON.stringify({ occ: true, recurring: true, sync_failed: true }),
    },
  ];
}

function buildDriveQuery(): string {
  const escaped = SPREADSHEET_NAME.replace(/'/g, "\\'");
  return [
    `name = '${escaped}'`,
    `mimeType = 'application/vnd.google-apps.spreadsheet'`,
    `trashed = false`,
  ].join(' and ');
}

function buildPointerDriveQuery(): string {
  const escaped = POINTER_SPREADSHEET_NAME.replace(/'/g, "\\'");
  return [
    `name = '${escaped}'`,
    `mimeType = 'application/vnd.google-apps.spreadsheet'`,
    `trashed = false`,
  ].join(' and ');
}

/**
 * Read the Mochia Pointer (if any) and return the target spreadsheet id
 * it points to. Returns null when no pointer exists or the pointer is
 * malformed (corrupted manually by the user, etc.).
 *
 * Pointer layout (single tab `pointer`, single data row at row 2):
 *   row 1 (header): target_spreadsheet_id
 *   row 2 (data):   <the owner's Mochia Data spreadsheetId>
 */
async function readPointer(client: SheetsClient): Promise<string | null> {
  let list;
  try {
    list = await client.driveFilesList(buildPointerDriveQuery());
  } catch {
    return null;
  }
  const candidate = list.files.find((f) => f.name === POINTER_SPREADSHEET_NAME);
  if (!candidate) return null;
  let resp;
  try {
    resp = await client.valuesGet(candidate.id, 'pointer!A2:A2');
  } catch {
    return null;
  }
  const value = resp.values?.[0]?.[0];
  if (typeof value !== 'string' || value.length === 0) return null;
  return value;
}

/**
 * Create the Mochia Pointer in the user's own Drive on first join, or
 * update it if the target changed. Idempotent — a fully-present pointer
 * with the right target produces no writes.
 *
 * Failure here is non-fatal — the user's join still succeeded via the
 * localStorage override; we just couldn't persist the cross-context
 * pointer. They'll need to use the join URL again from another context.
 */
async function ensurePointer(client: SheetsClient, targetId: string): Promise<void> {
  let list;
  try {
    list = await client.driveFilesList(buildPointerDriveQuery());
  } catch {
    // eslint-disable-next-line no-console
    console.warn('[Mochia/bootstrap] ensurePointer: drive list failed (non-fatal)');
    return;
  }
  const existing = list.files.find((f) => f.name === POINTER_SPREADSHEET_NAME);

  if (existing) {
    // Already present — check if the target matches; rewrite if not.
    let current: string | null = null;
    try {
      const resp = await client.valuesGet(existing.id, 'pointer!A2:A2');
      current = (resp.values?.[0]?.[0] ?? '') || null;
    } catch {
      /* continue to overwrite */
    }
    if (current === targetId) return;
    try {
      await client.valuesUpdate(existing.id, 'pointer!A2:A2', [[targetId]]);
    } catch {
      // eslint-disable-next-line no-console
      console.warn('[Mochia/bootstrap] ensurePointer: update failed (non-fatal)');
    }
    return;
  }

  // Create from scratch.
  try {
    const created = await client.spreadsheetsCreate({
      properties: { title: POINTER_SPREADSHEET_NAME },
      sheets: [{ properties: { title: 'pointer' } }],
    });
    const sheetId = created.spreadsheetId;
    // Some clients return a default Sheet1 alongside our requested 'pointer'
    // tab; find the placeholder if one came back so we can delete it.
    const sheets = created.sheets ?? [];
    const placeholder = sheets.find(
      (s) => s.properties?.title !== 'pointer' && typeof s.properties?.sheetId === 'number',
    );
    if (placeholder?.properties?.sheetId !== undefined) {
      try {
        await client.spreadsheetsBatchUpdate(sheetId, {
          requests: [{ deleteSheet: { sheetId: placeholder.properties.sheetId } }],
        });
      } catch {
        /* placeholder cleanup is best-effort */
      }
    }
    await client.valuesUpdate(sheetId, 'pointer!A1:A1', [['target_spreadsheet_id']]);
    await client.valuesUpdate(sheetId, 'pointer!A2:A2', [[targetId]]);
  } catch {
    // eslint-disable-next-line no-console
    console.warn('[Mochia/bootstrap] ensurePointer: create failed (non-fatal)');
  }
}

export async function bootstrap(client: SheetsClient): Promise<string> {
  const result = await bootstrapWithMeta(client);
  return result.spreadsheetId;
}

export interface BootstrapOptions {
  /**
   * Override Drive search and use this spreadsheet id directly. Set when
   * the user joined a shared workbook via `?join=<id>` URL — see
   * `src/lib/sharing`. The spreadsheet must already exist and be shared
   * with the current user (write access).
   */
  sharedSpreadsheetId?: string | null;
}

/**
 * Reads categories/books/meta in one `valuesBatchGet` round-trip, feeds the
 * result into `verifyAndRepair` (skipping its internal fallback batchGet),
 * and returns the snapshot for the caller to reuse — or `null` when
 * `verifyAndRepair` topped up `meta` during this call, which means the
 * snapshot read before that write no longer reflects reality (Decision 4:
 * bootstrapWithMeta 回傳可重複使用的 meta 快照，並定義何時視為過期).
 */
async function verifyAndRepairWithPreload(
  client: SheetsClient,
  spreadsheetId: string,
): Promise<PreloadedBootstrapRows | null> {
  const catRange = tabRange('categories', { startRow: 2 });
  const bookRange = tabRange('books', { startRow: 2 });
  const metaRange = tabRange('meta', { startRow: 2 });
  const batch = await client.valuesBatchGet(spreadsheetId, [catRange, bookRange, metaRange]);
  const [catGet, bookGet, metaGet] = batch.valueRanges ?? [];
  const preloaded: PreloadedBootstrapRows = {
    categories: (catGet?.values ?? []) as string[][],
    books: (bookGet?.values ?? []) as string[][],
    meta: (metaGet?.values ?? []) as string[][],
  };
  const { metaWasSeeded } = await verifyAndRepair(client, spreadsheetId, preloaded);
  return metaWasSeeded ? null : preloaded;
}

export async function bootstrapWithMeta(
  client: SheetsClient,
  options: BootstrapOptions = {},
): Promise<BootstrapResult> {
  // 0. Shared-workbook override path — second user joined via owner's URL.
  // Skip Drive search; the spreadsheets scope reads any sheet by id when
  // the file is shared. verifyAndRepair is still safe (idempotent — no
  // appends if seed rows already present).
  if (options.sharedSpreadsheetId) {
    // eslint-disable-next-line no-console
    console.info('[Mochia/bootstrap] Using shared spreadsheet override', {
      id: options.sharedSpreadsheetId,
    });
    const preloaded = await verifyAndRepairWithPreload(client, options.sharedSpreadsheetId);
    // Persist a Mochia Pointer in this user's OWN Drive so future cold
    // loads (especially from a separate-storage iOS PWA context) find the
    // shared workbook automatically. No-op if the pointer already exists.
    await ensurePointer(client, options.sharedSpreadsheetId);
    return { spreadsheetId: options.sharedSpreadsheetId, created: false, preloaded };
  }

  // 0b. Pointer-sheet path — a previous join populated this user's Drive
  // with a "Mochia Pointer" file containing the shared workbook id. This
  // makes the join survive iOS Safari's PWA/Safari storage split (the
  // localStorage override is per-partition; the pointer is on Drive,
  // visible from any context the user signs into).
  const pointerTarget = await readPointer(client);
  if (pointerTarget) {
    // eslint-disable-next-line no-console
    console.info('[Mochia/bootstrap] Following Mochia Pointer', { target: pointerTarget });
    const preloaded = await verifyAndRepairWithPreload(client, pointerTarget);
    return { spreadsheetId: pointerTarget, created: false, preloaded };
  }

  // 1. Look for an existing Mochia Data
  const list = await client.driveFilesList(buildDriveQuery());
  const candidates = list.files.filter((f) => f.name === SPREADSHEET_NAME);
  // Diagnostic — surfaces in browser console so the user can verify which
  // workbook the app is bound to (and catch the "duplicate Mochia Data"
  // case where reads + writes might split across sibling files).
  // eslint-disable-next-line no-console
  console.info('[Mochia/bootstrap] Drive search', {
    candidateCount: candidates.length,
    candidates: candidates.map((f) => ({ id: f.id, name: f.name })),
  });
  const existing = candidates[0];
  if (existing) {
    // Re-bootstrap path: a previous run may have crashed mid-seed. Top up any
    // missing seed rows. No-op when fully seeded.
    const preloaded = await verifyAndRepairWithPreload(client, existing.id);
    return { spreadsheetId: existing.id, created: false, preloaded };
  }

  // 2. Create new — let Sheets pick its own sheetIds; we'll add ours via
  //    batchUpdate + addSheet (so we don't need to know ids in advance) and
  //    delete the placeholder Sheet1.
  const createResp = await client.spreadsheetsCreate({
    properties: { title: SPREADSHEET_NAME },
  });
  const spreadsheetId = createResp.spreadsheetId;
  const placeholderId = createResp.sheets?.[0]?.properties?.sheetId;

  // 3. addSheet for each of the 6 tabs + delete the placeholder.
  const addRequests: Array<Record<string, unknown>> = TAB_NAMES.map((name) => ({
    addSheet: { properties: { title: name } },
  }));
  const requests: Array<Record<string, unknown>> = [...addRequests];
  if (typeof placeholderId === 'number') {
    requests.push({ deleteSheet: { sheetId: placeholderId } });
  }
  await client.spreadsheetsBatchUpdate(spreadsheetId, { requests });

  // 4. Write headers to each tab via valuesUpdate
  for (const name of TAB_NAMES) {
    const tab = TABS[name as TabName];
    await client.valuesUpdate(spreadsheetId, headerRange(tab.name), [tab.headers as string[]]);
  }

  // 5. Seed via verifyAndRepair — single source of truth, idempotent. No
  // preloaded snapshot here: the tabs were just created, so there's nothing
  // to reuse (Decision 4: preloaded is null on the first-time creation path).
  await verifyAndRepair(client, spreadsheetId);

  return { spreadsheetId, created: true, preloaded: null };
}

function buildCategoryRows(
  seed: ReadonlyArray<SeedCategory>,
  now: string,
): string[][] {
  const headers = TABS.categories.headers;
  return seed.map((c) =>
    objectToRow(headers, {
      id: c.id,
      // System seeds carry book_id='' — they're visible in every book.
      book_id: '',
      parent_id: '',
      name: c.name,
      kind: c.kind,
      color_token: c.color_token,
      icon: c.icon,
      sort_order: String(c.sort_order),
      is_system: 'true',
      rev: '1',
      created_at: now,
      updated_at: now,
      deleted_at: '',
    }),
  );
}

function buildSubCategoryRows(
  seed: ReadonlyArray<SeedSubCategory>,
  parentColor: Map<string, string>,
  now: string,
): string[][] {
  const headers = TABS.categories.headers;
  return seed.map((s) =>
    objectToRow(headers, {
      id: s.id,
      book_id: '',
      parent_id: s.parent_id,
      name: s.name,
      kind: s.kind,
      // Subs inherit color from their parent so the picker swatches match.
      color_token: parentColor.get(s.parent_id) ?? '--cat-other',
      icon: s.icon,
      sort_order: String(s.sort_order),
      is_system: 'true',
      rev: '1',
      created_at: now,
      updated_at: now,
      deleted_at: '',
    }),
  );
}

function buildBookRow(seed: SeedBook, now: string): string[] {
  return objectToRow(TABS.books.headers, {
    id: seed.id,
    name: seed.name,
    color_token: seed.color_token,
    type: seed.type,
    is_default: seed.is_default,
    rev: '1',
    created_at: now,
    updated_at: now,
    deleted_at: '',
  });
}

/**
 * Diff the expected seed rows against what's actually in the workbook and
 * append any that are missing. Idempotent — a fully-seeded sheet produces
 * no writes (and a single `valuesGet` round-trip per tab to verify).
 *
 * Sharp edges:
 *  - We never edit existing rows. Only append missing ones.
 *  - We match by stable identifiers (`id` for category/book, `key` for meta)
 *    so that a row the user manually edited still counts as "present".
 *  - When more than one tab needs writes, we coalesce them into a single
 *    `valuesAppend` per tab. Per-tab writes (rather than one batchUpdate) is
 *    the simplest way to share the append/insert-row semantics that
 *    `valuesBatchUpdate` does not offer for `INSERT_ROWS`.
 */
export interface PreloadedBootstrapRows {
  categories: string[][];
  books: string[][];
  meta: string[][];
}

export interface VerifyAndRepairResult {
  /**
   * True when this run appended one or more missing keys to the `meta`
   * tab. Signals to callers (see `bootstrapWithMeta`) that any
   * previously-read `meta` snapshot is now stale — `schema_version` in
   * particular may have just been seeded — and must not be reused for a
   * downstream schema-version check.
   */
  metaWasSeeded: boolean;
}

export async function verifyAndRepair(
  client: SheetsClient,
  spreadsheetId: string,
  preloaded?: PreloadedBootstrapRows,
): Promise<VerifyAndRepairResult> {
  const now = nowIso();

  const catRange = tabRange('categories', { startRow: 2 });
  const bookRange = tabRange('books', { startRow: 2 });
  const metaRange = tabRange('meta', { startRow: 2 });

  // Single round-trip for all three tabs' current contents (data-layer spec
  // requirement "Bootstrap Read Deduplication") — replaces the prior three
  // sequential `valuesGet` calls. Callers that already have a fresh batch
  // read (e.g. `bootstrapWithMeta`'s existing-spreadsheet path) pass it via
  // `preloaded` so this function doesn't re-fetch what was just fetched.
  let catExisting: string[][];
  let bookExisting: string[][];
  let metaExisting: string[][];
  if (preloaded) {
    catExisting = preloaded.categories;
    bookExisting = preloaded.books;
    metaExisting = preloaded.meta;
  } else {
    const batch = await client.valuesBatchGet(spreadsheetId, [catRange, bookRange, metaRange]);
    const [catGet, bookGet, metaGet] = batch.valueRanges ?? [];
    catExisting = (catGet?.values ?? []) as string[][];
    bookExisting = (bookGet?.values ?? []) as string[][];
    metaExisting = (metaGet?.values ?? []) as string[][];
  }

  // Categories
  //
  // Per change `harden-bootstrap-reseed-safety` (data-layer spec requirement
  // "Categories Seed Top-up Skipped When Sheet Has Been Bootstrapped"):
  // when the sheet already has any categories row, we treat the sheet as
  // past-first-bootstrap and SKIP the seed top-up. Without this guard, a
  // stale-bundle cold-load whose `SEED_*CATEGORIES` lists ids the user has
  // intentionally removed (via in-app delete or one-shot migration) would
  // resurrect them on next reload. `catExisting.length > 0` is the chosen
  // signal (design Decision 1 — simplest, no new schema). The guard is
  // scoped to categories only — books/meta keep their existing top-up
  // behaviour (design Decision 2). Implemented purely client-side; no
  // sheet-level barrier (design Decision 3).
  if (catExisting.length === 0) {
    // Fresh sheet — seed every SEED row in one append.
    const parentColor = new Map(SEED_CATEGORIES.map((c) => [c.id, c.color_token]));
    const rows = [
      ...buildCategoryRows(SEED_CATEGORIES, now),
      ...buildSubCategoryRows(SEED_SUBCATEGORIES, parentColor, now),
    ];
    await client.valuesAppend(spreadsheetId, `${TABS.categories.name}!A1`, rows);
  }

  // Books
  // Append every missing seed book. Idempotent — existing rows (including
  // the legacy `book-personal` seed from v2 workbooks) are left untouched
  // so user-renamed books stay user-renamed. We never re-default an
  // existing book that the user has flipped to non-default; the very first
  // bootstrap places `is_default=true` on 家庭 and that's the only time we
  // write that cell.
  const bookHeaders = TABS.books.headers;
  const presentBookIds = new Set<string>();
  let anyExistingBook = false;
  for (const row of bookExisting) {
    const obj = rowToObject(bookHeaders, row);
    const id = obj['id'];
    if (id) {
      presentBookIds.add(id);
      anyExistingBook = true;
    }
  }
  const missingBooks = SEED_BOOKS.filter((b) => !presentBookIds.has(b.id));
  if (missingBooks.length > 0) {
    // If the workbook already has a user-default book, don't slap a second
    // default on top — demote 家庭 to non-default during re-bootstrap so we
    // honour what's already there.
    const seedRows = missingBooks.map((b) => {
      const seed: SeedBook = anyExistingBook
        ? { ...b, is_default: 'false' }
        : b;
      return buildBookRow(seed, now);
    });
    await client.valuesAppend(spreadsheetId, `${TABS.books.name}!A1`, seedRows);
  }

  // Meta
  const presentMetaKeys = new Set<string>();
  for (const row of metaExisting) {
    const k = row[0] ?? '';
    if (k) presentMetaKeys.add(k);
  }
  const expectedMeta = buildSeedMeta();
  const missingMeta = expectedMeta.filter((m) => !presentMetaKeys.has(m.key));
  if (missingMeta.length > 0) {
    await client.valuesAppend(
      spreadsheetId,
      `${TABS.meta.name}!A1`,
      missingMeta.map((m) => [m.key, m.value]),
    );
  }
  return { metaWasSeeded: missingMeta.length > 0 };
}
